import React from 'react';

import { useSelector } from 'react-redux';

import { EDetailsMode } from 'components/Pages/Admin/Itinerary/Details/types';
import { useBlock } from 'components/shared/BlockProvider';
import LandingPageContext from 'components/shared/LandingPage/contexts/LandingPageContext';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useRect } from 'hooks/useRect';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { LANDING_THEMES } from 'utils/itineraries/functions';

export const useToolbarOffset = (isFocused: boolean): { top: number } => {
  const { blockId, pageHeaderRef } = useBlock();
  const { landingTheme } = React.useContext(LandingPageContext);
  const { isMobile, isMiddleScreen } = useMediaBreakpoints();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const pageHeaderRect = useRect(pageHeaderRef, ...[isFocused]);
  const isChronological = itinerary.detailsMode === EDetailsMode.Chronological;

  const blockStickyTopOffset = React.useMemo(() => {
    const screenSize = isMobile ? 'mobile' : isMiddleScreen ? 'tablet' : 'desktop';
    if (isChronological) {
      return getToolbarOffsetForChronological(screenSize, pageHeaderRect);
    } else {
      return getToolbarOffsetForOriginal(screenSize, landingTheme, blockId, pageHeaderRect);
    }
  }, [isFocused]);

  return { top: blockStickyTopOffset };
};

const getToolbarOffsetForOriginal = (
  screenSize: 'mobile' | 'tablet' | 'desktop',
  landingTheme: string | null,
  blockId: string,
  pageHeaderRect: DOMRect,
) => {
  const landingHeaderElem = document.getElementById('landingHeader');
  const mobileNavElem = document.getElementById('mobileNav');
  const landingHeaderHeight = landingHeaderElem?.getBoundingClientRect().height || 0;
  const mobileNavHeight = mobileNavElem?.getBoundingClientRect().height || 0;
  const pageHeaderHeight = pageHeaderRect?.height || 0;
  const pageBorderLine = 2;

  const isMobile = screenSize === 'mobile';
  const isDefaultTheme = landingTheme === LANDING_THEMES.default;
  const isBoatsTheme = landingTheme === LANDING_THEMES.boats;

  if (isMobile) {
    const EXTRA_OFFSET = isDefaultTheme ? 40 : isBoatsTheme ? -40 : 40;
    return (
      landingHeaderHeight -
      mobileNavHeight +
      pageHeaderHeight -
      pageBorderLine +
      calculateToolbarOffset(blockId) +
      EXTRA_OFFSET
    );
  }

  return landingHeaderHeight + pageHeaderHeight - pageBorderLine + calculateToolbarOffset(blockId);
};

const getToolbarOffsetForChronological = (screenSize: 'mobile' | 'tablet' | 'desktop', pageHeaderRect: DOMRect) => {
  const pageHeaderHeight = pageHeaderRect?.height || 0;

  const isMobileOrTablet = screenSize === 'mobile' || screenSize === 'tablet';
  if (isMobileOrTablet) {
    return 10;
  }

  return pageHeaderHeight + 10;
};

const calculateToolbarOffset = (id: string): number => {
  const BASE_OFFSET = 10;

  const rootElement = document.getElementById(id);
  if (!rootElement) return BASE_OFFSET;

  const hasStickyClass = (node: Element) =>
    Array.from(node.classList).some((className) => className.includes('sticky'));
  const stickyElements = Array.from(rootElement.childNodes)
    .filter((node): node is Element => node instanceof Element)
    .filter(hasStickyClass);

  const totalHeight = stickyElements
    .flatMap((el) => Array.from(el.getClientRects()))
    .reduce((sum, rect) => sum + (rect?.height || 0), 0);

  return totalHeight + BASE_OFFSET;
};
